import { useState, useEffect, useRef } from 'react';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer, getReservations, getProductByBarcode, getCategories } from '../lib/api';
import { Plus, Search, Edit2, Trash2, Users, Eye, BookmarkCheck, Copy, Check, ScanBarcode, Truck, EyeOff, FileSpreadsheet, Download, Camera, Clock, Phone, ChevronDown, ChevronUp, ArrowRightLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { useRefresh } from '../components/Layout';

// Format price with Romanian locale (comma as decimal separator)
const formatPrice = (value) => {
    if (value === undefined || value === null) return '0';
    // If it's a whole number, show without decimals
    if (Number.isInteger(value)) return value.toString();
    // Otherwise show with 2 decimals using comma
    return value.toFixed(2).replace('.', ',');
};

// Generate random barcode
const generateBarcode = () => {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `INT${timestamp}${random}`;
};

export const Customers = ({ privacyMode = false }) => {
    const { refreshStats } = useRefresh();
    const [customers, setCustomers] = useState([]);
    const [customerTotals, setCustomerTotals] = useState({});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isAddReservationOpen, setIsAddReservationOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [viewingCustomer, setViewingCustomer] = useState(null);
    const [customerReservations, setCustomerReservations] = useState([]);
    const [allCheckedItems, setAllCheckedItems] = useState({});
    const [checkedItemsLoaded, setCheckedItemsLoaded] = useState(false);
    
    // Load checked items from API on mount
    useEffect(() => {
        const loadCheckedItems = async () => {
            try {
                const res = await api.get('/checked-items');
                setAllCheckedItems(res.data.checked || {});
            } catch {
                // Fallback to localStorage
                try {
                    const saved = localStorage.getItem('orderCheckedItems');
                    if (saved) setAllCheckedItems(JSON.parse(saved));
                } catch {}
            }
            setCheckedItemsLoaded(true);
        };
        loadCheckedItems();
    }, []);
    
    // Save checked items to API (debounced)
    const saveCheckedItemsRef = useRef(null);
    useEffect(() => {
        if (!checkedItemsLoaded) return;
        
        if (saveCheckedItemsRef.current) {
            clearTimeout(saveCheckedItemsRef.current);
        }
        saveCheckedItemsRef.current = setTimeout(async () => {
            try {
                await api.post('/checked-items', { checked: allCheckedItems });
            } catch (err) {
                console.error('Failed to save checked items:', err);
            }
        }, 500);
    }, [allCheckedItems, checkedItemsLoaded]);
    const [formData, setFormData] = useState({
        customer_id: '',
        name: '',
        phone: '',
        address: '',
        notes: ''
    });
    const [copiedCustomerId, setCopiedCustomerId] = useState(null);
    const [showAllCustomers, setShowAllCustomers] = useState(false);
    
    // Add reservation state
    const [selectedCustomerForReservation, setSelectedCustomerForReservation] = useState(null);
    const [barcodeInput, setBarcodeInput] = useState('');
    const [currentProduct, setCurrentProduct] = useState(null);
    const [reserveQuantity, setReserveQuantity] = useState('1');
    const [finalPrice, setFinalPrice] = useState('');
    const [step, setStep] = useState(1);
    const barcodeInputRef = useRef(null);
    
    // New product form state
    const [showNewProductForm, setShowNewProductForm] = useState(false);
    const [newProductBarcode, setNewProductBarcode] = useState('');
    const [newProductName, setNewProductName] = useState('');
    const [newProductPrice, setNewProductPrice] = useState('');
    const [newProductCategory, setNewProductCategory] = useState('');
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    
    // Stock location selection
    const [stockLocations, setStockLocations] = useState(null);
    const [selectedFromLocation, setSelectedFromLocation] = useState('');
    
    // Edit reservation state
    const [isEditReservationOpen, setIsEditReservationOpen] = useState(false);
    const [editingReservation, setEditingReservation] = useState(null);
    const [editResQty, setEditResQty] = useState('1');
    const [editResPrice, setEditResPrice] = useState('');
    
    // Phone required modal
    const [showPhoneRequiredModal, setShowPhoneRequiredModal] = useState(false);
    const [phoneRequiredCustomerId, setPhoneRequiredCustomerId] = useState('');
    const [phoneRequiredInput, setPhoneRequiredInput] = useState('');
    
    // Categories autocomplete
    const [categories, setCategories] = useState([]);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    
    // Product names autocomplete
    const [productNames, setProductNames] = useState([]);
    const [showProductNameDropdown, setShowProductNameDropdown] = useState(false);
    
    // Cancel reservation modal state
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [cancellingReservation, setCancellingReservation] = useState(null);
    const [cancelReturnTo, setCancelReturnTo] = useState('magazin');
    const [cancelPalletLocation, setCancelPalletLocation] = useState('P01');
    
    // Delete order modal state (keeps customer, removes reservations)
    const [isDeleteOrderModalOpen, setIsDeleteOrderModalOpen] = useState(false);
    const [deletingOrderCustomer, setDeletingOrderCustomer] = useState(null);
    const [deleteReturnTo, setDeleteReturnTo] = useState('magazin');
    const [deletePalletLocation, setDeletePalletLocation] = useState('P01');
    
    // Collapsible sections state (default: expanded)
    const [isOrdersExpanded, setIsOrdersExpanded] = useState(true);
    const [isSafeOrdersExpanded, setIsSafeOrdersExpanded] = useState(true);
    
    // Move reservations state
    const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
    const [selectedForMove, setSelectedForMove] = useState([]);
    const [moveTargetCustomerId, setMoveTargetCustomerId] = useState('');
    const [moveTargetOrderNumber, setMoveTargetOrderNumber] = useState(null);
    const [availableOrders, setAvailableOrders] = useState([]);
    const [moveToNewOrder, setMoveToNewOrder] = useState(true);

    // Load categories on mount
    useEffect(() => {
        loadCategories();
        loadProductNames();
    }, []);
    
    // Close category dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showCategoryDropdown && !e.target.closest('[data-category-dropdown]')) {
                setShowCategoryDropdown(false);
            }
            if (showProductNameDropdown && !e.target.closest('[data-product-name-dropdown]')) {
                setShowProductNameDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showCategoryDropdown]);
    
    const loadCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data);
        } catch (err) {
            console.error('Error loading categories:', err);
        }
    };

    const loadProductNames = async () => {
        try {
            const response = await api.get('/products/names');
            setProductNames(response.data);
        } catch (err) {
            console.error('Error loading product names:', err);
        }
    };

    // Open edit reservation modal
    const openEditReservationModal = (res) => {
        setEditingReservation(res);
        // Handle both single reservations and grouped items
        const qty = res.totalQuantity || res.quantity;
        const price = res.final_price || res.product?.price || 0;
        setEditResQty(qty.toString());
        setEditResPrice(price.toString());
        setIsEditReservationOpen(true);
    };

    // Handle edit reservation
    const handleEditReservation = async () => {
        if (!editingReservation) return;
        
        try {
            // Handle grouped items (multiple reservationIds)
            const ids = editingReservation.reservationIds || [editingReservation.id];
            const newQty = parseInt(editResQty);
            const newPrice = parseFloat(editResPrice) || 0;
            
            // For grouped items, update the first reservation with new qty and delete the rest
            if (ids.length > 1) {
                // Delete all but first
                for (let i = 1; i < ids.length; i++) {
                    await api.delete(`/reservations/${ids[i]}`);
                }
            }
            
            // Update the first/only reservation
            await api.put(`/reservations/${ids[0]}`, {
                quantity: newQty,
                final_price: newPrice
            });
            
            toast.success('Rezervare actualizată');
            setIsEditReservationOpen(false);
            // Reload customer reservations
            if (viewingCustomer) {
                const res = await getReservations(viewingCustomer.customer_id);
                setCustomerReservations(res.data.filter(r => r.status === 'pending'));
            }
            loadCustomers(search);
            refreshStats();
        } catch (err) {
            toast.error('Eroare la actualizare');
            console.error(err);
        }
    };

    // Delete reservation item - now opens cancel modal
    const handleDeleteReservationItem = (reservation) => {
        setCancellingReservation(reservation);
        setCancelReturnTo('magazin');
        setCancelPalletLocation('P01');
        setIsCancelModalOpen(true);
    };
    
    // Confirm cancel reservation with return location
    const confirmCancelReservation = async () => {
        if (!cancellingReservation) return;
        
        try {
            // Handle grouped items (multiple reservationIds)
            const ids = cancellingReservation.reservationIds || [cancellingReservation.id];
            
            for (const id of ids) {
                await api.post(`/reservations/${id}/cancel`, {
                    return_to: cancelReturnTo,
                    pallet_location: cancelReturnTo === 'depozit' ? cancelPalletLocation : null
                });
            }
            
            toast.success('Rezervare anulată');
            setIsCancelModalOpen(false);
            setCancellingReservation(null);
            
            // Reload customer reservations
            if (viewingCustomer) {
                const res = await getReservations(viewingCustomer.customer_id);
                setCustomerReservations(res.data.filter(r => r.status === 'pending'));
            }
            loadCustomers(search);
            refreshStats();
        } catch (err) {
            toast.error('Eroare la anularea rezervării');
            console.error(err);
        }
    };

    // Open scanner
    const openScanner = () => {
        setIsScannerOpen(true);
    };

    // Close scanner and stop camera
    const closeScanner = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach(t => t.stop());
            setCameraStream(null);
        }
        setIsScannerOpen(false);
    };

    // Handle barcode from scanner
    const handleScannedBarcode = async (barcode) => {
        closeScanner();
        setBarcodeInput(barcode);
        
        try {
            const response = await getProductByBarcode(barcode);
            if (response.data) {
                setCurrentProduct(response.data);
                setFinalPrice(response.data.price?.toString() || '');
                setStep(2);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setNewProductBarcode(barcode);
                setShowNewProductForm(true);
            } else {
                toast.error('Eroare la căutarea produsului');
            }
        }
    };

    useEffect(() => {
        loadCustomers();
    }, [privacyMode]); // Reload when privacy mode changes

    const loadCustomers = async (searchTerm = '') => {
        try {
            setLoading(true);
            // Pass privacyMode to backend to filter hidden customers
            const response = await getCustomers(searchTerm, privacyMode);
            setCustomers(response.data);
            
            const totals = {};
            for (const customer of response.data) {
                try {
                    const resResponse = await getReservations(customer.customer_id, 'pending');
                    const total = resResponse.data.reduce((sum, res) => {
                        const price = res.final_price || res.product?.price || 0;
                        return sum + (price * res.quantity);
                    }, 0);
                    totals[customer.customer_id] = {
                        total,
                        count: resResponse.data.length,
                        reservations: resResponse.data
                    };
                } catch {
                    totals[customer.customer_id] = { total: 0, count: 0, reservations: [] };
                }
            }
            setCustomerTotals(totals);
        } catch (err) {
            toast.error('Eroare la încărcarea clienților');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Filter customers based on showAllCustomers toggle
    // Note: Hidden customers are already filtered by backend when privacyMode is true
    const filteredCustomers = customers
        .filter(c => {
            // Show all customers if toggle is on, otherwise only those with reservations
            if (showAllCustomers) return true;
            const totals = customerTotals[c.customer_id] || { count: 0 };
            return totals.count > 0;
        });

    const handleSearch = (e) => {
        e.preventDefault();
        loadCustomers(search);
    };

    const toggleHidden = async (customer) => {
        try {
            const response = await api.put(`/customers/${customer.customer_id}/toggle-hidden`);
            toast.success(response.data.is_hidden ? 'Client marcat ca sigur' : 'Client vizibil');
            loadCustomers(search);
        } catch (err) {
            toast.error('Eroare la actualizare');
        }
    };

    const openModal = (customer = null) => {
        if (customer) {
            setEditingCustomer(customer);
            setFormData({
                customer_id: customer.customer_id,
                name: customer.name || '',
                phone: customer.phone || '',
                address: customer.address || '',
                notes: customer.notes || ''
            });
        } else {
            setEditingCustomer(null);
            setFormData({
                customer_id: '',
                name: '',
                phone: '',
                address: '',
                notes: ''
            });
            setExistingCustomerFound(null);
        }
        setIsModalOpen(true);
    };

    // State for existing customer detection
    const [existingCustomerFound, setExistingCustomerFound] = useState(null);

    // Check if customer exists when ID is entered
    const handleCustomerIdChange = async (value) => {
        setFormData({ ...formData, customer_id: value });
        
        if (value.length >= 3) {
            // Check if customer already exists
            const existingCustomer = customers.find(c => c.customer_id === value);
            if (existingCustomer) {
                // Pre-fill customer data
                setFormData({
                    customer_id: value,
                    name: existingCustomer.name || '',
                    phone: existingCustomer.phone || '',
                    address: existingCustomer.address || '',
                    notes: existingCustomer.notes || ''
                });
                setExistingCustomerFound(existingCustomer);
            } else {
                setExistingCustomerFound(null);
            }
        } else {
            setExistingCustomerFound(null);
        }
    };

    const openViewModal = async (customer) => {
        setViewingCustomer(customer);
        setIsViewModalOpen(true);
        
        try {
            const response = await getReservations(customer.customer_id, 'pending');
            setCustomerReservations(response.data);
            
            // Check if we have saved checks for this customer
            const savedChecks = allCheckedItems[customer.customer_id];
            
            if (savedChecks) {
                // Use saved checks (user already started checking this order)
                // But also add any new products with TRANSPORT checked by default
                const grouped = groupReservationsByName(response.data);
                const updatedChecks = { ...savedChecks };
                grouped.forEach(item => {
                    const key = item.product?.name || item.product_id;
                    if (updatedChecks[key] === undefined) {
                        // New product - check if it's transport
                        updatedChecks[key] = item.product?.barcode === 'TRANSPORT';
                    }
                });
                setAllCheckedItems(prev => ({
                    ...prev,
                    [customer.customer_id]: updatedChecks
                }));
            } else {
                // First time opening - TRANSPORT is checked by default
                const initialChecked = {};
                const grouped = groupReservationsByName(response.data);
                grouped.forEach(item => {
                    const key = item.product?.name || item.product_id;
                    initialChecked[key] = item.product?.barcode === 'TRANSPORT';
                });
                setAllCheckedItems(prev => ({
                    ...prev,
                    [customer.customer_id]: initialChecked
                }));
            }
        } catch (err) {
            console.error(err);
        }
    };
    
    // Get checked items for current customer
    const getCheckedItems = () => {
        if (!viewingCustomer) return {};
        return allCheckedItems[viewingCustomer.customer_id] || {};
    };
    
    // Helper to group reservations by name for checklist
    const groupReservationsByName = (reservations) => {
        const grouped = {};
        reservations.forEach((res) => {
            const key = res.product?.name || res.product_id;
            if (!grouped[key]) {
                grouped[key] = {
                    ...res,
                    totalQuantity: res.quantity,
                    totalPrice: (res.final_price || res.product?.price || 0) * res.quantity,
                    reservationIds: [res.id]
                };
            } else {
                grouped[key].totalQuantity += res.quantity;
                grouped[key].totalPrice += (res.final_price || res.product?.price || 0) * res.quantity;
                grouped[key].reservationIds.push(res.id);
            }
        });
        return Object.values(grouped);
    };
    
    // Toggle item check
    const toggleItemCheck = (itemName) => {
        if (!viewingCustomer) return;
        setAllCheckedItems(prev => ({
            ...prev,
            [viewingCustomer.customer_id]: {
                ...(prev[viewingCustomer.customer_id] || {}),
                [itemName]: !(prev[viewingCustomer.customer_id]?.[itemName])
            }
        }));
    };
    
    // Check all items
    const toggleAllItems = () => {
        if (!viewingCustomer) return;
        const grouped = groupReservationsByName(customerReservations);
        
        // Check if all are currently checked
        const checkedItems = getCheckedItems();
        const allCurrentlyChecked = grouped.every(item => {
            const key = item.product?.name || item.product_id;
            return checkedItems[key] === true;
        });
        
        if (allCurrentlyChecked) {
            // Uncheck all
            setAllCheckedItems(prev => ({
                ...prev,
                [viewingCustomer.customer_id]: {}
            }));
        } else {
            // Check all
            const allChecked = {};
            grouped.forEach(item => {
                const key = item.product?.name || item.product_id;
                allChecked[key] = true;
            });
            setAllCheckedItems(prev => ({
                ...prev,
                [viewingCustomer.customer_id]: allChecked
            }));
        }
    };
    
    // Check if all items are checked
    const allItemsChecked = () => {
        const checkedItems = getCheckedItems();
        const grouped = groupReservationsByName(customerReservations);
        return grouped.every(item => {
            const key = item.product?.name || item.product_id;
            return checkedItems[key] === true;
        });
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingCustomer(null);
        setFormData({
            customer_id: '',
            name: '',
            phone: '',
            address: '',
            notes: ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingCustomer) {
                await updateCustomer(editingCustomer.customer_id, formData);
                toast.success('Client actualizat cu succes');
                closeModal();
                loadCustomers(search);
            } else if (existingCustomerFound) {
                // Client existent - adaugă doar taxa transport pentru comandă nouă
                if (!formData.is_hidden) {
                    let transportProduct;
                    try {
                        const transportRes = await getProductByBarcode('TRANSPORT');
                        transportProduct = transportRes.data;
                    } catch {
                        const createRes = await api.post('/products', {
                            name: 'Taxa Transport',
                            barcode: 'TRANSPORT',
                            price: 25,
                            category: 'Transport'
                        });
                        transportProduct = createRes.data;
                    }
                    
                    await api.post('/reservations-direct', {
                        product_id: transportProduct.id,
                        customer_id: formData.customer_id,
                        quantity: 1,
                        final_price: 25,
                        notes: 'Taxa transport - comandă nouă'
                    });
                    
                    toast.success(`Comandă nouă pentru ${formData.customer_id} + Taxa transport 25 RON`);
                } else {
                    toast.success(`Comandă nouă pentru ${formData.customer_id}`);
                }
                
                closeModal();
                loadCustomers(search);
                
                // Deschide comanda pentru a adăuga produse
                setTimeout(() => {
                    openViewModal(existingCustomerFound);
                }, 300);
            } else {
                // Client nou - obține următorul număr de ordine global
                let nextOrderNumber = 1;
                try {
                    const orderNumRes = await api.get('/orders/next-global-number');
                    nextOrderNumber = orderNumRes.data.next_order_number;
                } catch (err) {
                    console.error('Error getting order number:', err);
                }
                
                const newCustomerData = await createCustomer(formData);
                
                // Nu adăuga taxa transport pentru clienții ascunși (cu bifă)
                if (!formData.is_hidden) {
                    let transportProduct;
                    try {
                        const transportRes = await getProductByBarcode('TRANSPORT');
                        transportProduct = transportRes.data;
                    } catch {
                        const createRes = await api.post('/products', {
                            name: 'Taxa Transport',
                            barcode: 'TRANSPORT',
                            price: 25,
                            category: 'Transport'
                        });
                        transportProduct = createRes.data;
                    }
                    
                    await api.post('/reservations-direct', {
                        product_id: transportProduct.id,
                        customer_id: formData.customer_id,
                        quantity: 1,
                        final_price: 25,
                        notes: 'Taxa transport',
                        order_number: nextOrderNumber
                    });
                    
                    toast.success(`Comanda #${nextOrderNumber} creată + Taxa transport 25 RON`);
                } else {
                    // Pentru clienți siguri, creăm o rezervare placeholder pentru a avea order_number
                    // Aceasta va fi înlocuită cu primul produs adăugat
                    toast.success(`Comanda #${nextOrderNumber} creată (fără taxă transport)`);
                }
                
                closeModal();
                loadCustomers(search);
                
                // Deschide comanda pentru a adăuga produse
                setTimeout(() => {
                    openViewModal({ 
                        customer_id: formData.customer_id,
                        name: formData.name,
                        phone: formData.phone,
                        address: formData.address,
                        is_hidden: formData.is_hidden
                    });
                }, 300);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la salvarea clientului');
            console.error(err);
        }
    };

    const handleDelete = (customer) => {
        // Check if customer has reservations
        const total = customerTotals[customer.customer_id];
        if (total && total.count > 0) {
            // Has products - show modal to ask where to move them
            setDeletingOrderCustomer(customer);
            setDeleteReturnTo('magazin');
            setDeletePalletLocation('P01');
            setIsDeleteOrderModalOpen(true);
        } else {
            // No products - just confirm and delete customer
            if (window.confirm(`Sigur dorești să ștergi clientul "${customer.customer_id}"?`)) {
                deleteCustomerDirect(customer.customer_id);
            }
        }
    };
    
    const deleteCustomerDirect = async (customerId) => {
        try {
            await deleteCustomer(customerId);
            toast.success('Client șters cu succes');
            loadCustomers(search);
            refreshStats();
        } catch (err) {
            toast.error('Eroare la ștergerea clientului');
            console.error(err);
        }
    };
    
    const confirmDeleteOrder = async () => {
        if (!deletingOrderCustomer) return;
        
        try {
            const response = await api.post(`/customers/${deletingOrderCustomer.customer_id}/delete`, {
                return_to: deleteReturnTo,
                pallet_location: deleteReturnTo === 'depozit' ? deletePalletLocation : null
            });
            toast.success(response.data.message);
            setIsDeleteOrderModalOpen(false);
            setDeletingOrderCustomer(null);
            loadCustomers(search);
            refreshStats();
        } catch (err) {
            toast.error('Eroare la ștergerea comenzii');
            console.error(err);
        }
    };

    const getCustomerTotal = (reservations) => {
        return reservations.reduce((sum, res) => {
            const price = res.final_price || res.product?.price || 0;
            return sum + (price * res.quantity);
        }, 0);
    };

    // Group reservations by product_id, summing quantities
    // Group reservations by product NAME (not ID) to combine products with same description
    const groupReservations = (reservations) => {
        const grouped = {};
        reservations.forEach((res) => {
            // Use product name as key to group identical products
            const key = res.product?.name || res.product_id;
            if (!grouped[key]) {
                grouped[key] = {
                    ...res,
                    totalQuantity: res.quantity,
                    totalPrice: (res.final_price || res.product?.price || 0) * res.quantity,
                    reservationIds: [res.id]
                };
            } else {
                grouped[key].totalQuantity += res.quantity;
                grouped[key].totalPrice += (res.final_price || res.product?.price || 0) * res.quantity;
                grouped[key].reservationIds.push(res.id);
            }
        });
        return Object.values(grouped);
    };

    const generateSimpleCopyText = (reservations) => {
        const grouped = groupReservations(reservations);
        let text = '';
        grouped.forEach((item) => {
            text += `${item.product?.name || 'Produs'} x${item.totalQuantity} - ${formatPrice(item.totalPrice)} RON\n`;
        });
        const grandTotal = getCustomerTotal(reservations);
        text += `\nTOTAL: ${formatPrice(grandTotal)} RON`;
        return text;
    };

    const handleCopyOrder = async (customer) => {
        const totals = customerTotals[customer.customer_id];
        if (!totals || totals.reservations.length === 0) {
            toast.error('Nu există rezervări de copiat');
            return;
        }
        
        const text = generateSimpleCopyText(totals.reservations);
        
        try {
            await navigator.clipboard.writeText(text);
            setCopiedCustomerId(customer.customer_id);
            toast.success('Lista copiată!');
            setTimeout(() => setCopiedCustomerId(null), 2000);
        } catch (err) {
            toast.error('Nu s-a putut copia');
        }
    };

    // Complete order and generate Excel
    const handleCompleteOrder = async (customer) => {
        const totals = customerTotals[customer.customer_id];
        if (!totals || totals.reservations.length === 0) {
            toast.error('Nu există rezervări de finalizat');
            return;
        }
        
        if (!window.confirm(`Finalizezi comanda pentru ${customer.customer_id}? (${formatPrice(totals.total)} RON)`)) return;
        
        try {
            // First, assign order number to this batch of reservations
            const reservationIds = totals.reservations.map(r => r.id);
            await api.put('/reservations/batch-order-number', {
                customer_id: customer.customer_id,
                reservation_ids: reservationIds
            });
            
            // Then mark all reservations as completed
            for (const res of totals.reservations) {
                await api.put(`/reservations/${res.id}/status?status=completed`);
            }
            
            // Download Excel report
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            window.open(`${backendUrl}/api/export/customer-order/${customer.customer_id}?status=completed`, '_blank');
            
            toast.success('Comandă finalizată! Raportul Excel se descarcă...');
            loadCustomers(search);
        } catch (err) {
            toast.error('Eroare la finalizarea comenzii');
            console.error(err);
        }
    };

    // Toggle order status (waiting <-> informed)
    const handleToggleOrderStatus = async (customer) => {
        const totals = customerTotals[customer.customer_id];
        if (!totals || totals.count === 0) return;
        
        // Get current status from first reservation
        const currentStatus = totals.reservations[0]?.order_status || 'waiting';
        const newStatus = currentStatus === 'waiting' ? 'informed' : 'waiting';
        
        try {
            await api.put('/reservations/batch-order-status', {
                customer_id: customer.customer_id,
                order_status: newStatus
            });
            
            // Update local state
            setCustomerTotals(prev => ({
                ...prev,
                [customer.customer_id]: {
                    ...prev[customer.customer_id],
                    reservations: prev[customer.customer_id].reservations.map(r => ({
                        ...r,
                        order_status: newStatus
                    }))
                }
            }));
            
            toast.success(newStatus === 'informed' ? 'Client informat' : 'Marcat în așteptare');
        } catch (err) {
            toast.error('Eroare la actualizarea stării');
            console.error(err);
        }
    };

    // Download Excel for active order
    const handleDownloadExcel = (customer) => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        window.open(`${backendUrl}/api/export/customer-order/${customer.customer_id}?status=all`, '_blank');
        toast.success('Se descarcă raportul Excel...');
    };

    // Move reservations functions
    const openMoveModal = (reservations) => {
        // Get all reservation IDs from grouped items
        const reservationIds = [];
        reservations.forEach(item => {
            if (item.reservations) {
                item.reservations.forEach(r => reservationIds.push(r.id));
            } else if (item.id) {
                reservationIds.push(item.id);
            }
        });
        setSelectedForMove(reservationIds);
        setMoveTargetCustomerId(viewingCustomer?.customer_id || '');
        setMoveToNewOrder(true);
        setMoveTargetOrderNumber(null);
        setAvailableOrders([]);
        setIsMoveModalOpen(true);
    };
    
    const loadCustomerOrders = async (customerId) => {
        if (!customerId) return;
        try {
            const response = await api.get(`/customers/${customerId}/orders`);
            setAvailableOrders(response.data || []);
        } catch (err) {
            console.error('Error loading orders:', err);
            setAvailableOrders([]);
        }
    };
    
    const handleMoveReservations = async () => {
        if (!moveTargetCustomerId || selectedForMove.length === 0) {
            toast.error('Selectați clientul destinație');
            return;
        }
        
        try {
            const response = await api.post('/reservations/move', {
                reservation_ids: selectedForMove,
                target_customer_id: moveTargetCustomerId,
                target_order_number: moveToNewOrder ? null : moveTargetOrderNumber
            });
            
            toast.success(response.data.message);
            setIsMoveModalOpen(false);
            setIsViewModalOpen(false);
            loadCustomers();
        } catch (err) {
            toast.error('Eroare la mutarea produselor');
            console.error(err);
        }
    };

    // Add reservation functions
    const openAddReservation = (customer) => {
        setSelectedCustomerForReservation(customer);
        setStep(1);
        setBarcodeInput('');
        setCurrentProduct(null);
        setReserveQuantity('1');
        setFinalPrice('');
        setShowNewProductForm(false);
        setNewProductBarcode('');
        setNewProductName('');
        setNewProductPrice('');
        setNewProductCategory('');
        setIsAddReservationOpen(true);
        
        setTimeout(() => {
            if (barcodeInputRef.current) {
                barcodeInputRef.current.focus();
            }
        }, 100);
    };

    const handleBarcodeKeyDown = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            
            if (!barcodeInput.trim()) {
                setNewProductBarcode(generateBarcode());
                setShowNewProductForm(true);
                return;
            }
            
            try {
                const response = await getProductByBarcode(barcodeInput.trim());
                if (response.data) {
                    setCurrentProduct(response.data);
                    setFinalPrice(response.data.price?.toString() || '');
                    
                    // Load stock locations
                    try {
                        const locResponse = await api.get(`/products/${response.data.id}/stock-locations`);
                        setStockLocations(locResponse.data);
                        // Auto-select if only one location
                        if (locResponse.data.depozit_locations?.length === 1) {
                            setSelectedFromLocation(locResponse.data.depozit_locations[0].location);
                        } else {
                            setSelectedFromLocation('');
                        }
                    } catch (locErr) {
                        setStockLocations(null);
                    }
                    
                    setStep(2);
                }
            } catch (err) {
                if (err.response?.status === 404) {
                    setNewProductBarcode(barcodeInput.trim());
                    setShowNewProductForm(true);
                } else {
                    toast.error('Eroare la căutarea produsului');
                }
            }
        }
    };

    const handleAddReservation = async () => {
        if (!currentProduct || !selectedCustomerForReservation) return;
        
        try {
            // Use new endpoint that checks magazin/depozit automatically
            const requestData = {
                product_id: currentProduct.id,
                quantity: parseInt(reserveQuantity),
                final_price: parseFloat(finalPrice) || 0
            };
            
            // Add from_location if selected
            if (selectedFromLocation) {
                requestData.from_location = selectedFromLocation;
            }
            
            const response = await api.post(`/customers/${selectedCustomerForReservation.customer_id}/add-product`, requestData);
            
            const source = response.data.source;
            let message = `"${currentProduct.name}" adăugat`;
            if (source === 'magazin') {
                message += ' (din Magazin)';
            } else if (source.includes('depozit')) {
                message += ` (${source})`;
            } else if (source === 'palet') {
                message += ' (de pe Palet)';
            }
            toast.success(message);
            
            // Check if customer needs phone - show modal
            if (response.data?.customer_needs_phone) {
                setPhoneRequiredCustomerId(selectedCustomerForReservation.customer_id);
                setPhoneRequiredInput('');
                setShowPhoneRequiredModal(true);
            }
            
            setBarcodeInput('');
            setCurrentProduct(null);
            setReserveQuantity('1');
            setFinalPrice('');
            setStep(1);
            setShowNewProductForm(false);
            setStockLocations(null);
            setSelectedFromLocation('');
            
            loadCustomers(search);
            refreshStats(); // Update sidebar
            
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                }
            }, 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la adăugare');
            console.error(err);
        }
    };

    const handleCreateNewProduct = async () => {
        if (!newProductName.trim() || !selectedCustomerForReservation) {
            toast.error('Introdu numele produsului');
            return;
        }
        
        try {
            const barcode = newProductBarcode.trim() || generateBarcode();
            const price = parseFloat(newProductPrice) || 0;
            
            // Use new endpoint that creates product and adds to customer automatically
            const response = await api.post(`/customers/${selectedCustomerForReservation.customer_id}/add-product`, {
                barcode: barcode,
                quantity: parseInt(reserveQuantity),
                final_price: parseFloat(finalPrice) || price,
                new_product_name: newProductName.trim(),
                new_product_price: price,
                new_product_category: newProductCategory.trim() || null
            });
            
            toast.success(`"${newProductName.trim()}" creat și adăugat direct la client`);
            
            // Check if customer needs phone - show modal
            if (response.data?.customer_needs_phone) {
                setPhoneRequiredCustomerId(selectedCustomerForReservation.customer_id);
                setPhoneRequiredInput('');
                setShowPhoneRequiredModal(true);
            }
            
            setBarcodeInput('');
            setNewProductBarcode('');
            setNewProductName('');
            setNewProductPrice('');
            setNewProductCategory('');
            setReserveQuantity('1');
            setFinalPrice('');
            setShowNewProductForm(false);
            setStep(1);
            
            loadCustomers(search);
            refreshStats(); // Update sidebar
            
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                }
            }, 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la crearea produsului');
            console.error(err);
        }
    };
    
    // Save phone number for customer (from phone required modal)
    const handleSavePhoneRequired = async () => {
        if (!phoneRequiredCustomerId || !phoneRequiredInput.trim()) {
            toast.error('Introdu numărul de telefon');
            return;
        }
        try {
            await updateCustomer(phoneRequiredCustomerId, { phone: phoneRequiredInput.trim() });
            toast.success('Telefon salvat');
            setShowPhoneRequiredModal(false);
            setPhoneRequiredInput('');
            setPhoneRequiredCustomerId('');
            loadCustomers(search); // Refresh to update card colors
        } catch (err) {
            toast.error('Eroare la salvare telefon');
            console.error(err);
        }
    };

    return (
        <div data-testid="customers-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Comenzi
                    </h1>
                    <p className="text-zinc-500 mt-1">
                        Comenzi active TikTok
                        {privacyMode && <span className="text-red-500 ml-2">(Safe Mode)</span>}
                    </p>
                </div>
                <Button
                    onClick={() => openModal()}
                    data-testid="add-customer-button"
                    className="bg-zinc-900 hover:bg-zinc-800 text-white h-12 px-6"
                >
                    <Plus size={20} className="mr-2" />
                    Comandă Nouă
                </Button>
            </div>

            {/* Quick Stats Bar */}
            <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-violet-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {formatPrice(Object.values(customerTotals).reduce((sum, t) => sum + (t?.total || 0), 0))}
                    </div>
                    <p className="text-xs text-violet-500">RON Comenzi</p>
                </div>
                <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-zinc-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {customers.length}
                    </div>
                    <p className="text-xs text-zinc-500">Clienți Total</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {customers.filter(c => (customerTotals[c.customer_id]?.count || 0) > 0).length}
                    </div>
                    <p className="text-xs text-emerald-500">Clienți Activi</p>
                </div>
            </div>

            {/* Search */}
            <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                    <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Caută după ID, nume sau telefon..."
                        data-testid="customer-search-input"
                        className="pl-10 h-12 bg-white"
                    />
                </div>
            </form>
            
            {/* Toggle to show all customers */}
            <div className="flex items-center justify-between mb-4 bg-white border border-zinc-200 rounded-lg p-3">
                <span className="text-sm text-zinc-600">Arată toți clienții (inclusiv fără comenzi active)</span>
                <button
                    onClick={() => setShowAllCustomers(!showAllCustomers)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                        showAllCustomers ? 'bg-violet-500' : 'bg-zinc-300'
                    }`}
                >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        showAllCustomers ? 'left-7' : 'left-1'
                    }`} />
                </button>
            </div>

            {/* Customers List */}
            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-lg p-4">
                            <Skeleton className="h-12 w-12 rounded-full mb-3" />
                            <Skeleton className="h-4 w-1/2 mb-2" />
                            <Skeleton className="h-3 w-3/4" />
                        </div>
                    ))}
                </div>
            ) : filteredCustomers.length === 0 ? (
                <div className="empty-state bg-white border border-zinc-200 rounded-lg">
                    <Users size={64} className="text-zinc-300" />
                    <h3 className="text-lg font-medium text-zinc-900 mt-4">
                        {privacyMode ? 'Niciun client vizibil' : 'Niciun client'}
                    </h3>
                    <p className="text-zinc-500 mt-1">
                        {privacyMode ? 'Toți clienții sunt ascunși în Safe Mode' : 'Adaugă primul client'}
                    </p>
                    {!privacyMode && (
                        <Button onClick={() => openModal()} className="mt-4 bg-zinc-900 hover:bg-zinc-800">
                            <Plus size={20} className="mr-2" />
                            Client Nou
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Regular Orders Section - FIRST */}
                    {filteredCustomers.filter(c => !c.is_hidden).length > 0 && (
                        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setIsOrdersExpanded(!isOrdersExpanded)}
                                className="w-full px-4 py-3 flex items-center justify-between bg-violet-50 hover:bg-violet-100 transition-colors"
                            >
                                <h2 className="text-lg font-semibold text-violet-700 flex items-center gap-2">
                                    <BookmarkCheck size={18} />
                                    Comenzi ({filteredCustomers.filter(c => !c.is_hidden).length})
                                </h2>
                                {isOrdersExpanded ? <ChevronUp size={20} className="text-violet-500" /> : <ChevronDown size={20} className="text-violet-500" />}
                            </button>
                            {isOrdersExpanded && (
                                <div className="p-3 space-y-3">
                                    {filteredCustomers.filter(c => !c.is_hidden).map((customer, index) => {
                                        const totals = customerTotals[customer.customer_id] || { total: 0, count: 0, reservations: [] };
                                        const orderNum = totals.reservations[0]?.order_number || (index + 1);
                                        return (
                                            <CustomerCard
                                                key={customer.customer_id}
                                                customer={customer}
                                                totals={totals}
                                                orderNumber={orderNum}
                                                formatPrice={formatPrice}
                                                openAddReservation={openAddReservation}
                                                openViewModal={openViewModal}
                                                handleCompleteOrder={handleCompleteOrder}
                                                handleToggleOrderStatus={handleToggleOrderStatus}
                                                openModal={openModal}
                                                handleCopyOrder={handleCopyOrder}
                                                handleDelete={handleDelete}
                                                toggleHidden={toggleHidden}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Safe Orders Section - SECOND, Hidden in Safe Mode */}
                    {!privacyMode && filteredCustomers.filter(c => c.is_hidden).length > 0 && (
                        <div className="bg-white border border-emerald-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setIsSafeOrdersExpanded(!isSafeOrdersExpanded)}
                                className="w-full px-4 py-3 flex items-center justify-between bg-emerald-50 hover:bg-emerald-100 transition-colors"
                            >
                                <h2 className="text-lg font-semibold text-emerald-700 flex items-center gap-2">
                                    <Check size={18} />
                                    Comenzi Sigure ({filteredCustomers.filter(c => c.is_hidden).length})
                                </h2>
                                {isSafeOrdersExpanded ? <ChevronUp size={20} className="text-emerald-500" /> : <ChevronDown size={20} className="text-emerald-500" />}
                            </button>
                            {isSafeOrdersExpanded && (
                                <div className="p-3 space-y-3">
                                    {filteredCustomers.filter(c => c.is_hidden).map((customer, index) => {
                                        const totals = customerTotals[customer.customer_id] || { total: 0, count: 0, reservations: [] };
                                        const orderNum = totals.reservations[0]?.order_number || (index + 1);
                                        return (
                                            <CustomerCard
                                                key={customer.customer_id}
                                                customer={customer}
                                                totals={totals}
                                                orderNumber={orderNum}
                                                formatPrice={formatPrice}
                                                openAddReservation={openAddReservation}
                                                openViewModal={openViewModal}
                                                handleCompleteOrder={handleCompleteOrder}
                                                handleToggleOrderStatus={handleToggleOrderStatus}
                                                openModal={openModal}
                                                handleCopyOrder={handleCopyOrder}
                                                handleDelete={handleDelete}
                                                toggleHidden={toggleHidden}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Add/Edit Customer Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {editingCustomer ? 'Editează Client' : 'Comandă Nouă'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                        <div>
                            <Label htmlFor="customer_id">ID Comandă (ultimele cifre telefon) *</Label>
                            <Input
                                id="customer_id"
                                value={formData.customer_id}
                                onChange={(e) => handleCustomerIdChange(e.target.value)}
                                placeholder="Ex: 123 sau 1234"
                                required
                                disabled={!!editingCustomer}
                                className="mt-1"
                                autoFocus
                            />
                            {!editingCustomer && existingCustomerFound && (
                                <p className="text-xs text-blue-600 mt-1 font-medium">
                                    Client existent găsit - se adaugă comandă nouă + Taxa transport
                                </p>
                            )}
                            {!editingCustomer && formData.customer_id && !existingCustomerFound && formData.customer_id.length >= 3 && (
                                <p className="text-xs text-emerald-600 mt-1">
                                    <Truck size={12} className="inline mr-1" />
                                    Client nou - Taxa transport 25 RON se adaugă automat
                                </p>
                            )}
                        </div>
                        
                        <div>
                            <Label htmlFor="phone">Telefon</Label>
                            <Input
                                id="phone"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="Ex: 0721234567"
                                className="mt-1"
                            />
                        </div>
                        
                        <div>
                            <Label htmlFor="name">Nume</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ex: Maria Popescu"
                                className="mt-1"
                            />
                        </div>
                        
                        <div>
                            <Label htmlFor="address">Adresă Livrare</Label>
                            <Input
                                id="address"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                placeholder="Ex: Str. Exemplu nr. 1, București"
                                className="mt-1"
                            />
                        </div>
                        
                        <div>
                            <Label htmlFor="notes">Note</Label>
                            <Input
                                id="notes"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Ex: preferă livrare dimineața"
                                className="mt-1"
                            />
                        </div>
                        
                        {/* Client Sigur checkbox - only for new customers */}
                        {!editingCustomer && (
                            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <input
                                    type="checkbox"
                                    id="is_hidden"
                                    checked={formData.is_hidden || false}
                                    onChange={(e) => setFormData({ ...formData, is_hidden: e.target.checked })}
                                    className="w-5 h-5 text-green-600 rounded border-green-300 focus:ring-green-500"
                                />
                                <label htmlFor="is_hidden" className="flex-1 cursor-pointer">
                                    <span className="font-medium text-green-700">Client Sigur</span>
                                    <p className="text-xs text-green-600">Fără taxă transport, nu apare pe paleți</p>
                                </label>
                            </div>
                        )}
                        
                        <div className="flex gap-3 pt-4">
                            <Button type="button" variant="outline" onClick={closeModal} className="flex-1">
                                Anulează
                            </Button>
                            <Button type="submit" className="flex-1 bg-zinc-900 hover:bg-zinc-800">
                                {editingCustomer ? 'Salvează' : (
                                    <span className="flex items-center gap-2">
                                        <Plus size={16} />
                                        {existingCustomerFound ? 'Continuă cu Produse' : 'Creează și Adaugă Produse'}
                                    </span>
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* View Customer Details Modal */}
            <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Client #{viewingCustomer?.customer_id}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {viewingCustomer && (
                        <div className="mt-4">
                            <div className="bg-zinc-50 rounded-lg p-4 mb-4">
                                {viewingCustomer.phone && <p className="font-medium text-zinc-900">{viewingCustomer.phone}</p>}
                                {viewingCustomer.name && <p className="text-zinc-600">{viewingCustomer.name}</p>}
                                {viewingCustomer.address && <p className="text-sm text-zinc-500 mt-1">Adresă: {viewingCustomer.address}</p>}
                                {viewingCustomer.notes && <p className="text-sm text-violet-600 mt-1">Note: {viewingCustomer.notes}</p>}
                            </div>
                            
                            {customerReservations.length === 0 ? (
                                <div className="text-center py-8">
                                    <BookmarkCheck size={40} className="mx-auto text-zinc-300 mb-2" />
                                    <p className="text-zinc-500">Nicio rezervare activă</p>
                                </div>
                            ) : (
                                <>
                                    {/* Toggle All Button */}
                                    <div className="flex justify-end mb-2">
                                        <button
                                            onClick={toggleAllItems}
                                            className="text-xs text-violet-600 hover:text-violet-800 font-medium"
                                        >
                                            {allItemsChecked() ? '✗ Debifează tot' : '✓ Bifează tot'}
                                        </button>
                                    </div>
                                    
                                    <div className="space-y-1 max-h-48 overflow-y-auto border border-zinc-200 rounded-lg p-2">
                                        {groupReservations(customerReservations).map((item) => {
                                            const isTransport = item.product?.barcode === 'TRANSPORT';
                                            const itemKey = item.product?.name || item.product_id;
                                            const checkedItems = getCheckedItems();
                                            const isChecked = checkedItems[itemKey] === true;
                                            
                                            return (
                                                <div 
                                                    key={item.product_id} 
                                                    className={`flex justify-between items-center py-1 px-2 rounded cursor-pointer ${
                                                        isChecked 
                                                            ? 'bg-emerald-100' 
                                                            : isTransport 
                                                                ? 'bg-amber-50' 
                                                                : 'hover:bg-zinc-50'
                                                    }`}
                                                    onClick={() => toggleItemCheck(itemKey)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                                            isChecked 
                                                                ? 'bg-emerald-500 border-emerald-500 text-white' 
                                                                : 'border-zinc-300'
                                                        }`}>
                                                            {isChecked && <Check size={12} />}
                                                        </div>
                                                        <span className={`text-sm ${
                                                            isChecked 
                                                                ? 'text-emerald-800' 
                                                                : isTransport 
                                                                    ? 'text-amber-700' 
                                                                    : 'text-zinc-900'
                                                        }`}>
                                                            {item.product?.name}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <span className={isChecked ? 'text-emerald-600' : 'text-zinc-500'}>x{item.totalQuantity}</span>
                                                        <span className={`font-semibold ${
                                                            isChecked 
                                                                ? 'text-emerald-700' 
                                                                : isTransport 
                                                                    ? 'text-amber-700' 
                                                                    : 'text-zinc-900'
                                                        }`}>
                                                            {formatPrice(item.totalPrice)} RON
                                                        </span>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); openEditReservationModal(item); }}
                                                            className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                                                            title="Editează"
                                                        >
                                                            <Edit2 size={14} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                openMoveModal([item]); 
                                                            }}
                                                            className="p-1 text-amber-500 hover:bg-amber-50 rounded"
                                                            title="Mută la altă comandă"
                                                        >
                                                            <ArrowRightLeft size={14} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteReservationItem(item); }}
                                                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                                                            title="Șterge"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    
                                    {/* Move Selected Button */}
                                    {customerReservations.length > 1 && (
                                        <Button
                                            onClick={() => openMoveModal(groupReservations(customerReservations))}
                                            variant="outline"
                                            className="w-full mt-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                                        >
                                            <ArrowRightLeft size={16} className="mr-2" />
                                            Mută Produse la Altă Comandă
                                        </Button>
                                    )}
                                    
                                    {/* Summary */}
                                    <div className="mt-3 p-3 bg-zinc-100 rounded-lg">
                                        <div className="flex justify-between items-center text-sm text-zinc-600 mb-2">
                                            <span>Produse (fără transport):</span>
                                            <span className="font-medium">
                                                {customerReservations.filter(r => r.product?.barcode !== 'TRANSPORT').reduce((sum, r) => sum + r.quantity, 0)} buc.
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="font-semibold text-zinc-700">TOTAL:</span>
                                            <span className="text-xl font-bold text-violet-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                {formatPrice(getCustomerTotal(customerReservations))} RON
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <Button
                                        onClick={() => {
                                            const text = generateSimpleCopyText(customerReservations);
                                            navigator.clipboard.writeText(text);
                                            toast.success('Lista copiată!');
                                        }}
                                        className="w-full mt-3 bg-violet-500 hover:bg-violet-600"
                                    >
                                        <Copy size={18} className="mr-2" />
                                        Copiază Lista
                                    </Button>
                                    
                                    {/* Add Product Button */}
                                    <Button
                                        onClick={() => {
                                            setIsViewModalOpen(false);
                                            openAddReservation(viewingCustomer);
                                        }}
                                        variant="outline"
                                        className="w-full mt-2 border-violet-300 text-violet-700 hover:bg-violet-50"
                                    >
                                        <Plus size={18} className="mr-2" />
                                        Adaugă Produs
                                    </Button>
                                    
                                    {/* Close Order Button */}
                                    <Button
                                        onClick={() => {
                                            handleCompleteOrder(viewingCustomer);
                                            setIsViewModalOpen(false);
                                        }}
                                        className="w-full mt-2 bg-emerald-500 hover:bg-emerald-600"
                                    >
                                        <Check size={18} className="mr-2" />
                                        ✓ Gata
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Add Reservation Modal */}
            <Dialog open={isAddReservationOpen} onOpenChange={setIsAddReservationOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Adaugă Produs - #{selectedCustomerForReservation?.customer_id}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {step === 1 && !showNewProductForm && (
                        <div className="space-y-4 mt-4">
                            <div>
                                <Label className="flex items-center gap-2">
                                    <ScanBarcode size={18} />
                                    Scanează sau tastează cod
                                </Label>
                                <div className="flex gap-2 mt-1">
                                    <Input
                                        ref={barcodeInputRef}
                                        value={barcodeInput}
                                        onChange={(e) => setBarcodeInput(e.target.value)}
                                        onKeyDown={handleBarcodeKeyDown}
                                        placeholder="Cod de bare..."
                                        className="h-12 font-mono flex-1"
                                        autoFocus
                                    />
                                    <Button
                                        type="button"
                                        onClick={openScanner}
                                        className="h-12 w-12 bg-[#E11D48] hover:bg-[#BE123C]"
                                        title="Deschide camera"
                                    >
                                        <Camera size={20} />
                                    </Button>
                                </div>
                                <p className="text-xs text-zinc-500 mt-1">Enter gol = produs nou</p>
                            </div>
                            
                            <Button
                                onClick={() => {
                                    setNewProductBarcode(generateBarcode());
                                    setShowNewProductForm(true);
                                }}
                                variant="outline"
                                className="w-full"
                            >
                                <Plus size={18} className="mr-2" />
                                Produs Nou (fără cod)
                            </Button>
                            
                            <Button onClick={() => setIsAddReservationOpen(false)} variant="outline" className="w-full">
                                Închide
                            </Button>
                        </div>
                    )}
                    
                    {showNewProductForm && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <p className="text-sm text-amber-700">Produs nou - se adaugă în stock + rezervare</p>
                            </div>
                            
                            <div>
                                <Label>Cod de Bare</Label>
                                <Input
                                    value={newProductBarcode}
                                    onChange={(e) => setNewProductBarcode(e.target.value)}
                                    placeholder="Auto-generat..."
                                    className="mt-1 font-mono"
                                />
                            </div>
                            
                            <div data-product-name-dropdown className="relative">
                                <Label>Nume Produs *</Label>
                                <Input
                                    value={newProductName}
                                    onChange={(e) => {
                                        setNewProductName(e.target.value);
                                        setShowProductNameDropdown(true);
                                    }}
                                    onFocus={() => setShowProductNameDropdown(true)}
                                    placeholder="Ex: Set pahare"
                                    className="mt-1"
                                    autoFocus
                                />
                                {showProductNameDropdown && productNames.length > 0 && (
                                    (() => {
                                        const filtered = productNames.filter(p => 
                                            p.name.toLowerCase().includes(newProductName.toLowerCase())
                                        ).slice(0, 10);
                                        return filtered.length > 0 && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                {filtered.map((p, idx) => (
                                                    <button
                                                        key={idx}
                                                        type="button"
                                                        className="w-full px-3 py-2 text-left hover:bg-zinc-100 text-sm flex justify-between items-center"
                                                        onClick={() => {
                                                            setNewProductName(p.name);
                                                            if (p.price) setNewProductPrice(p.price.toString());
                                                            if (p.category) setNewProductCategory(p.category);
                                                            setShowProductNameDropdown(false);
                                                        }}
                                                    >
                                                        <span>{p.name}</span>
                                                        {p.price && <span className="text-zinc-400 text-xs">{p.price} RON</span>}
                                                    </button>
                                                ))}
                                            </div>
                                        );
                                    })()
                                )}
                            </div>
                            
                            <div data-category-dropdown>
                                <Label>Categorie</Label>
                                <div className="relative mt-1">
                                    <Input
                                        value={newProductCategory}
                                        onChange={(e) => {
                                            setNewProductCategory(e.target.value);
                                            setShowCategoryDropdown(true);
                                        }}
                                        onFocus={() => setShowCategoryDropdown(true)}
                                        placeholder="Selectează sau scrie categorie..."
                                        autoComplete="off"
                                    />
                                    {showCategoryDropdown && categories.length > 0 && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                            {categories
                                                .filter(cat => !newProductCategory || cat.toLowerCase().includes(newProductCategory.toLowerCase()))
                                                .map((cat, i) => (
                                                    <button
                                                        key={i}
                                                        type="button"
                                                        onClick={() => {
                                                            setNewProductCategory(cat);
                                                            setShowCategoryDropdown(false);
                                                        }}
                                                        className={`w-full text-left px-3 py-2 hover:bg-zinc-100 text-sm ${
                                                            newProductCategory === cat ? 'bg-emerald-50 text-emerald-700 font-medium' : ''
                                                        }`}
                                                    >
                                                        {cat}
                                                    </button>
                                                ))
                                            }
                                            {categories.filter(cat => !newProductCategory || cat.toLowerCase().includes(newProductCategory.toLowerCase())).length === 0 && (
                                                <div className="px-3 py-2 text-sm text-zinc-500">
                                                    Categorie nouă: "{newProductCategory}"
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Cantitate</Label>
                                    <div className="flex items-center gap-1 mt-1">
                                        <button
                                            type="button"
                                            onClick={() => setReserveQuantity(prev => Math.max(1, parseInt(prev || 1) - 1).toString())}
                                            className="w-10 h-10 rounded-lg bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-lg font-bold text-zinc-600"
                                        >
                                            −
                                        </button>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={reserveQuantity}
                                            onChange={(e) => setReserveQuantity(e.target.value)}
                                            className="text-center"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setReserveQuantity(prev => (parseInt(prev || 1) + 1).toString())}
                                            className="w-10 h-10 rounded-lg bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-lg font-bold text-zinc-600"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <Label>Preț (RON)</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={newProductPrice}
                                        onChange={(e) => {
                                            setNewProductPrice(e.target.value);
                                            setFinalPrice(e.target.value);
                                        }}
                                        placeholder="0"
                                        className="mt-1"
                                    />
                                </div>
                            </div>
                            
                            <div className="flex gap-3">
                                <Button
                                    onClick={() => {
                                        setShowNewProductForm(false);
                                        setNewProductBarcode('');
                                        setNewProductName('');
                                        setNewProductPrice('');
                                        setNewProductCategory('');
                                    }}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Înapoi
                                </Button>
                                <Button
                                    onClick={handleCreateNewProduct}
                                    className="flex-1 bg-amber-500 hover:bg-amber-600"
                                    disabled={!newProductName.trim()}
                                >
                                    Creează + Adaugă
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    {step === 2 && currentProduct && !showNewProductForm && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-zinc-50 rounded-lg p-4">
                                <h3 className="font-semibold text-zinc-900">{currentProduct.name}</h3>
                                <p className="text-sm text-zinc-500">Preț estimat: ~{formatPrice(currentProduct.price)} RON</p>
                                
                                {/* Stock info */}
                                {stockLocations && (
                                    <div className="mt-2 pt-2 border-t border-zinc-200">
                                        <p className="text-xs text-zinc-400 mb-1">Stock disponibil:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {stockLocations.magazin_quantity > 0 && (
                                                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                                                    Magazin: {stockLocations.magazin_quantity}
                                                </span>
                                            )}
                                            {stockLocations.depozit_locations?.map(loc => (
                                                <span key={loc.location} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                                    {loc.location}: {loc.quantity}
                                                </span>
                                            ))}
                                            {stockLocations.magazin_quantity === 0 && (!stockLocations.depozit_locations || stockLocations.depozit_locations.length === 0) && (
                                                <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded">
                                                    De pe Palet (fără stock)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {/* Location selector - only if multiple depozit locations */}
                            {stockLocations && stockLocations.depozit_locations?.length > 1 && stockLocations.magazin_quantity === 0 && (
                                <div>
                                    <Label>De unde luăm? (Depozit)</Label>
                                    <select
                                        value={selectedFromLocation}
                                        onChange={(e) => setSelectedFromLocation(e.target.value)}
                                        className="w-full mt-1 h-10 px-3 border border-zinc-200 rounded-md"
                                    >
                                        <option value="">Automat (prima locație)</option>
                                        {stockLocations.depozit_locations.map(loc => (
                                            <option key={loc.location} value={loc.location}>
                                                {loc.location} ({loc.quantity} buc)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            <div>
                                <Label>Cantitate</Label>
                                <div className="flex items-center gap-1 mt-1">
                                    <button
                                        type="button"
                                        onClick={() => setReserveQuantity(prev => Math.max(1, parseInt(prev || 1) - 1).toString())}
                                        className="w-10 h-10 rounded-lg bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-lg font-bold text-zinc-600"
                                    >
                                        −
                                    </button>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={reserveQuantity}
                                        onChange={(e) => setReserveQuantity(e.target.value)}
                                        className="text-center"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setReserveQuantity(prev => (parseInt(prev || 1) + 1).toString())}
                                        className="w-10 h-10 rounded-lg bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-lg font-bold text-zinc-600"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            
                            <div>
                                <Label>Preț Final (RON)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={finalPrice}
                                    onChange={(e) => setFinalPrice(e.target.value)}
                                    placeholder="0"
                                    className="mt-1 text-lg font-bold"
                                    autoFocus
                                />
                            </div>
                            
                            <div className="flex gap-3">
                                <Button
                                    onClick={() => {
                                        setStep(1);
                                        setBarcodeInput('');
                                        setCurrentProduct(null);
                                        setStockLocations(null);
                                        setSelectedFromLocation('');
                                    }}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Înapoi
                                </Button>
                                <Button onClick={handleAddReservation} className="flex-1 bg-violet-500 hover:bg-violet-600">
                                    Adaugă
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            
            {/* Barcode Scanner */}
            <BarcodeScanner
                isOpen={isScannerOpen}
                onClose={closeScanner}
                onScan={handleScannedBarcode}
            />

            {/* Edit Reservation Modal */}
            <Dialog open={isEditReservationOpen} onOpenChange={setIsEditReservationOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Editează Rezervare
                        </DialogTitle>
                    </DialogHeader>
                    
                    {editingReservation && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-zinc-50 rounded-lg p-4">
                                <p className="font-medium text-zinc-900">{editingReservation.product?.name}</p>
                                <p className="text-xs text-zinc-500 font-mono mt-1">
                                    #{editingReservation.product?.barcode || 'N/A'}
                                </p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="editQty">Cantitate</Label>
                                    <Input
                                        id="editQty"
                                        type="number"
                                        min="1"
                                        value={editResQty}
                                        onChange={(e) => setEditResQty(e.target.value)}
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="editPrice">Preț Final (RON)</Label>
                                    <Input
                                        id="editPrice"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={editResPrice}
                                        onChange={(e) => setEditResPrice(e.target.value)}
                                        className="mt-1"
                                    />
                                </div>
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsEditReservationOpen(false)}
                                    className="flex-1"
                                >
                                    Anulează
                                </Button>
                                <Button
                                    onClick={handleEditReservation}
                                    className="flex-1 bg-blue-500 hover:bg-blue-600"
                                >
                                    Salvează
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Cancel Reservation Modal */}
            <Dialog open={isCancelModalOpen} onOpenChange={setIsCancelModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Anulează Rezervarea
                        </DialogTitle>
                    </DialogHeader>
                    
                    {cancellingReservation && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-zinc-100 p-3 rounded-lg">
                                <p className="font-medium text-zinc-900">
                                    {cancellingReservation.product?.name || 'Produs'}
                                </p>
                                <p className="text-sm text-zinc-600">
                                    Cantitate: <strong>{cancellingReservation.quantity}</strong> buc.
                                </p>
                            </div>
                            
                            <div>
                                <Label className="text-base font-medium">Ce faci cu produsele?</Label>
                                <div className="mt-3 space-y-2">
                                    <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                        cancelReturnTo === 'magazin' 
                                            ? 'border-emerald-500 bg-emerald-50' 
                                            : 'border-zinc-200 hover:border-zinc-300'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="returnTo"
                                            value="magazin"
                                            checked={cancelReturnTo === 'magazin'}
                                            onChange={(e) => setCancelReturnTo(e.target.value)}
                                            className="sr-only"
                                        />
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                            cancelReturnTo === 'magazin' ? 'border-emerald-500' : 'border-zinc-300'
                                        }`}>
                                            {cancelReturnTo === 'magazin' && (
                                                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-zinc-900">Mută în Magazin</p>
                                            <p className="text-xs text-zinc-500">Produsele vor fi disponibile în magazin</p>
                                        </div>
                                    </label>
                                    
                                    <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                        cancelReturnTo === 'depozit' 
                                            ? 'border-amber-500 bg-amber-50' 
                                            : 'border-zinc-200 hover:border-zinc-300'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="returnTo"
                                            value="depozit"
                                            checked={cancelReturnTo === 'depozit'}
                                            onChange={(e) => setCancelReturnTo(e.target.value)}
                                            className="sr-only"
                                        />
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                            cancelReturnTo === 'depozit' ? 'border-amber-500' : 'border-zinc-300'
                                        }`}>
                                            {cancelReturnTo === 'depozit' && (
                                                <div className="w-3 h-3 rounded-full bg-amber-500" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-zinc-900">Mută în Depozit</p>
                                            <p className="text-xs text-zinc-500">Produsele vor fi depozitate</p>
                                        </div>
                                    </label>
                                    
                                    {cancelReturnTo === 'depozit' && (
                                        <div className="ml-8 mt-2">
                                            <Label htmlFor="palletLocation" className="text-sm">Locație depozit</Label>
                                            <Input
                                                id="palletLocation"
                                                value={cancelPalletLocation}
                                                onChange={(e) => setCancelPalletLocation(e.target.value)}
                                                placeholder="P01, P02, etc."
                                                className="mt-1"
                                            />
                                        </div>
                                    )}
                                    
                                    <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                        cancelReturnTo === 'pierdut' 
                                            ? 'border-red-500 bg-red-50' 
                                            : 'border-zinc-200 hover:border-zinc-300'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="returnTo"
                                            value="pierdut"
                                            checked={cancelReturnTo === 'pierdut'}
                                            onChange={(e) => setCancelReturnTo(e.target.value)}
                                            className="sr-only"
                                        />
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                            cancelReturnTo === 'pierdut' ? 'border-red-500' : 'border-zinc-300'
                                        }`}>
                                            {cancelReturnTo === 'pierdut' && (
                                                <div className="w-3 h-3 rounded-full bg-red-500" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-zinc-900">Produse pierdute</p>
                                            <p className="text-xs text-zinc-500">Produsele nu se mai recuperează</p>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setIsCancelModalOpen(false);
                                        setCancellingReservation(null);
                                    }}
                                    className="flex-1"
                                >
                                    Renunță
                                </Button>
                                <Button
                                    onClick={confirmCancelReservation}
                                    className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                                >
                                    Anulează Rezervarea
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Order Modal */}
            <Dialog open={isDeleteOrderModalOpen} onOpenChange={setIsDeleteOrderModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Șterge Comanda
                        </DialogTitle>
                    </DialogHeader>
                    
                    {deletingOrderCustomer && (
                        <div className="space-y-4 mt-4">
                            <div className="bg-zinc-100 p-3 rounded-lg">
                                <p className="font-medium text-zinc-900">
                                    Client: #{deletingOrderCustomer.customer_id} {deletingOrderCustomer.name && `- ${deletingOrderCustomer.name}`}
                                </p>
                                <p className="text-sm text-zinc-600">
                                    Produse în comandă: <strong>{customerTotals[deletingOrderCustomer.customer_id]?.count || 0}</strong> buc.
                                </p>
                                <p className="text-sm text-zinc-600">
                                    Valoare comandă: <strong>{(customerTotals[deletingOrderCustomer.customer_id]?.total || 0).toFixed(2)} RON</strong>
                                </p>
                            </div>
                            
                            <div>
                                <Label className="text-base font-medium">Ce faci cu produsele?</Label>
                                <div className="mt-3 space-y-2">
                                    <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                        deleteReturnTo === 'magazin' 
                                            ? 'border-emerald-500 bg-emerald-50' 
                                            : 'border-zinc-200 hover:border-zinc-300'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="deleteReturnTo"
                                            value="magazin"
                                            checked={deleteReturnTo === 'magazin'}
                                            onChange={(e) => setDeleteReturnTo(e.target.value)}
                                            className="sr-only"
                                        />
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                            deleteReturnTo === 'magazin' ? 'border-emerald-500' : 'border-zinc-300'
                                        }`}>
                                            {deleteReturnTo === 'magazin' && (
                                                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-zinc-900">Mută în Magazin</p>
                                            <p className="text-xs text-zinc-500">Produsele vor fi disponibile în magazin</p>
                                        </div>
                                    </label>
                                    
                                    <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                        deleteReturnTo === 'depozit' 
                                            ? 'border-amber-500 bg-amber-50' 
                                            : 'border-zinc-200 hover:border-zinc-300'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="deleteReturnTo"
                                            value="depozit"
                                            checked={deleteReturnTo === 'depozit'}
                                            onChange={(e) => setDeleteReturnTo(e.target.value)}
                                            className="sr-only"
                                        />
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                            deleteReturnTo === 'depozit' ? 'border-amber-500' : 'border-zinc-300'
                                        }`}>
                                            {deleteReturnTo === 'depozit' && (
                                                <div className="w-3 h-3 rounded-full bg-amber-500" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-zinc-900">Mută în Depozit</p>
                                            <p className="text-xs text-zinc-500">Produsele vor fi depozitate</p>
                                        </div>
                                    </label>
                                    
                                    {deleteReturnTo === 'depozit' && (
                                        <div className="ml-8 mt-2">
                                            <Label htmlFor="deletePalletLocation" className="text-sm">Locație depozit</Label>
                                            <Input
                                                id="deletePalletLocation"
                                                value={deletePalletLocation}
                                                onChange={(e) => setDeletePalletLocation(e.target.value)}
                                                placeholder="P01, P02, etc."
                                                className="mt-1"
                                            />
                                        </div>
                                    )}
                                    
                                    <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                        deleteReturnTo === 'pierdut' 
                                            ? 'border-red-500 bg-red-50' 
                                            : 'border-zinc-200 hover:border-zinc-300'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="deleteReturnTo"
                                            value="pierdut"
                                            checked={deleteReturnTo === 'pierdut'}
                                            onChange={(e) => setDeleteReturnTo(e.target.value)}
                                            className="sr-only"
                                        />
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                            deleteReturnTo === 'pierdut' ? 'border-red-500' : 'border-zinc-300'
                                        }`}>
                                            {deleteReturnTo === 'pierdut' && (
                                                <div className="w-3 h-3 rounded-full bg-red-500" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-zinc-900">Produse pierdute</p>
                                            <p className="text-xs text-zinc-500">Produsele nu se mai recuperează</p>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setIsDeleteOrderModalOpen(false);
                                        setDeletingOrderCustomer(null);
                                    }}
                                    className="flex-1"
                                >
                                    Renunță
                                </Button>
                                <Button
                                    onClick={confirmDeleteOrder}
                                    className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                                >
                                    Șterge Comanda
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            
            {/* Move Reservations Modal */}
            <Dialog open={isMoveModalOpen} onOpenChange={setIsMoveModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <ArrowRightLeft className="inline mr-2" size={20} />
                            Mută Produse
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        <p className="text-sm text-zinc-600">
                            {selectedForMove.length} produs(e) selectate pentru mutare
                        </p>
                        
                        {/* Target Customer */}
                        <div>
                            <Label>Client destinație</Label>
                            <Input
                                value={moveTargetCustomerId}
                                onChange={(e) => {
                                    setMoveTargetCustomerId(e.target.value);
                                    loadCustomerOrders(e.target.value);
                                }}
                                placeholder="ID client (ex: 123)"
                                className="mt-1"
                            />
                        </div>
                        
                        {/* Order Type Selection */}
                        <div className="space-y-2">
                            <Label>Destinație comandă</Label>
                            
                            <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                moveToNewOrder 
                                    ? 'border-violet-500 bg-violet-50' 
                                    : 'border-zinc-200 hover:border-zinc-300'
                            }`}>
                                <input
                                    type="radio"
                                    checked={moveToNewOrder}
                                    onChange={() => setMoveToNewOrder(true)}
                                    className="sr-only"
                                />
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                    moveToNewOrder ? 'border-violet-500' : 'border-zinc-300'
                                }`}>
                                    {moveToNewOrder && <div className="w-3 h-3 rounded-full bg-violet-500" />}
                                </div>
                                <div>
                                    <p className="font-medium text-zinc-900">Comandă Nouă</p>
                                    <p className="text-xs text-zinc-500">Se va crea o comandă nouă cu număr automat</p>
                                </div>
                            </label>
                            
                            {availableOrders.length > 0 && availableOrders.map(order => (
                                <label 
                                    key={order.order_number}
                                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                                        !moveToNewOrder && moveTargetOrderNumber === order.order_number
                                            ? 'border-amber-500 bg-amber-50' 
                                            : 'border-zinc-200 hover:border-zinc-300'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        checked={!moveToNewOrder && moveTargetOrderNumber === order.order_number}
                                        onChange={() => {
                                            setMoveToNewOrder(false);
                                            setMoveTargetOrderNumber(order.order_number);
                                        }}
                                        className="sr-only"
                                    />
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                        !moveToNewOrder && moveTargetOrderNumber === order.order_number 
                                            ? 'border-amber-500' 
                                            : 'border-zinc-300'
                                    }`}>
                                        {!moveToNewOrder && moveTargetOrderNumber === order.order_number && (
                                            <div className="w-3 h-3 rounded-full bg-amber-500" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-zinc-900">
                                            Comandă #{order.order_number}
                                        </p>
                                        <p className="text-xs text-zinc-500">
                                            {order.items_count} produse • {order.total.toFixed(2)} RON
                                        </p>
                                    </div>
                                </label>
                            ))}
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => setIsMoveModalOpen(false)}
                                className="flex-1"
                            >
                                Renunță
                            </Button>
                            <Button
                                onClick={handleMoveReservations}
                                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                                disabled={!moveTargetCustomerId}
                            >
                                <ArrowRightLeft size={16} className="mr-2" />
                                Mută
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Phone Required Modal - shown when adding product to customer without phone */}
            <Dialog open={showPhoneRequiredModal} onOpenChange={setShowPhoneRequiredModal}>
                <DialogContent className="sm:max-w-md z-[200]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-600">
                            <Phone size={20} />
                            Telefon necesar
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-zinc-600">
                            Clientul <strong>{phoneRequiredCustomerId}</strong> nu are număr de telefon. 
                            Introdu telefonul pentru a continua:
                        </p>
                        <Input
                            type="tel"
                            placeholder="07xxxxxxxx"
                            value={phoneRequiredInput}
                            onChange={(e) => setPhoneRequiredInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && phoneRequiredInput.trim()) {
                                    handleSavePhoneRequired();
                                }
                            }}
                            autoFocus
                            className="text-lg"
                        />
                    </div>
                    <div className="flex gap-2 justify-end">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowPhoneRequiredModal(false);
                                setPhoneRequiredInput('');
                            }}
                        >
                            Mai târziu
                        </Button>
                        <Button
                            onClick={handleSavePhoneRequired}
                            disabled={!phoneRequiredInput.trim()}
                            className="bg-emerald-600 hover:bg-emerald-700"
                        >
                            Salvează telefon
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// CustomerCard component for cleaner code
const CustomerCard = ({ 
    customer, 
    totals, 
    orderNumber,
    formatPrice, 
    openAddReservation, 
    openViewModal, 
    handleCompleteOrder, 
    handleToggleOrderStatus,
    openModal, 
    handleCopyOrder, 
    handleDelete,
    toggleHidden
}) => {
    const [copiedCustomerId, setCopiedCustomerId] = useState(null);
    
    const handleCopy = async () => {
        await handleCopyOrder(customer);
        setCopiedCustomerId(customer.customer_id);
        setTimeout(() => setCopiedCustomerId(null), 2000);
    };
    
    // Determine customer status for background color
    const hasPhone = customer.phone && customer.phone.trim();
    const hasName = customer.name && customer.name.trim();
    const hasAddress = customer.address && customer.address.trim();
    
    // Background color logic:
    // - Green (hidden): completed/hidden order
    // - Green (not hidden): has phone AND (has name OR has address) = client complet
    // - Blue: has phone only (no name, no address) = client doar cu telefon
    // - Red: no phone = client necunoscut
    let cardBgClass = 'bg-white border-zinc-200'; // default
    if (customer.is_hidden) {
        cardBgClass = 'border-green-200 bg-green-50/30'; // Hidden - keep original
    } else if (!hasPhone) {
        cardBgClass = 'bg-red-50 border-red-200'; // No phone - RED
    } else if (!hasName && !hasAddress) {
        cardBgClass = 'bg-blue-50 border-blue-200'; // Phone only - BLUE
    } else {
        cardBgClass = 'bg-emerald-50 border-emerald-200'; // Complete - GREEN
    }
    
    return (
        <div
            data-testid={`customer-card-${customer.customer_id}`}
            className={`${cardBgClass} border rounded-lg p-3 sm:p-4 hover:shadow-sm transition-shadow`}
        >
            {/* Desktop Layout */}
            <div className="flex items-center gap-4">
                {/* Order Number + ID Circle */}
                <div className="flex flex-col items-center gap-1">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                        customer.is_hidden ? 'bg-green-200 text-green-700' : 'bg-violet-200 text-violet-700'
                    }`}>
                        #{orderNumber || '?'}
                    </span>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        customer.is_hidden ? 'bg-green-100' : 'bg-violet-100'
                    }`}>
                        <span className={`text-base font-bold ${
                            customer.is_hidden ? 'text-green-700' : 'text-violet-700'
                        }`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {customer.customer_id}
                        </span>
                    </div>
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        {customer.phone && (
                            <span className="font-semibold text-zinc-900">{customer.phone}</span>
                        )}
                        {customer.name && (
                            <span className="text-zinc-600">- {customer.name}</span>
                        )}
                        {!customer.phone && !customer.name && (
                            <span className="text-zinc-400">Fără detalii</span>
                        )}
                    </div>
                    {customer.address && (
                        <p className="text-sm text-zinc-500 truncate">{customer.address}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                        <BookmarkCheck size={14} className="text-violet-500" />
                        <span className="text-sm text-zinc-600">{totals.count} produse</span>
                    </div>
                </div>
                
                {/* Total & Actions */}
                <div className="flex flex-col items-end gap-2">
                    <span className="text-2xl font-bold text-violet-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {formatPrice(totals.total)} RON
                    </span>
                    <div className="flex items-center gap-1 flex-wrap justify-end">
                        <Button
                            onClick={() => openAddReservation(customer)}
                            size="sm"
                            variant="outline"
                            className="h-8 px-2"
                            title="Adaugă produs"
                        >
                            <Plus size={16} />
                        </Button>
                        <Button
                            onClick={handleCopy}
                            size="sm"
                            variant="outline"
                            className="h-8 px-2"
                            title="Copiază lista"
                        >
                            {copiedCustomerId === customer.customer_id ? (
                                <Check size={16} className="text-green-600" />
                            ) : (
                                <Copy size={16} />
                            )}
                        </Button>
                        <Button
                            onClick={() => openViewModal(customer)}
                            size="sm"
                            variant="outline"
                            className="h-8 px-2"
                            title="Vezi detalii"
                        >
                            <Eye size={16} />
                        </Button>
                        {/* Order Status Button */}
                        {totals.count > 0 && (
                            <Button
                                onClick={() => handleToggleOrderStatus(customer)}
                                size="sm"
                                className={`h-8 px-2 ${
                                    totals.reservations[0]?.order_status === 'informed'
                                        ? 'bg-blue-500 hover:bg-blue-600 text-white'
                                        : 'bg-amber-400 hover:bg-amber-500 text-amber-900'
                                }`}
                                title={totals.reservations[0]?.order_status === 'informed' ? 'Informat' : 'În așteptare'}
                            >
                                {totals.reservations[0]?.order_status === 'informed' ? (
                                    <><Phone size={14} className="mr-1" /> Inf</>
                                ) : (
                                    <><Clock size={14} className="mr-1" /> Așt</>
                                )}
                            </Button>
                        )}
                        <Button
                            onClick={() => handleCompleteOrder(customer)}
                            size="sm"
                            className="h-8 px-3 bg-emerald-500 hover:bg-emerald-600"
                            title="Finalizează comanda"
                            disabled={totals.count === 0}
                        >
                            ✓ Gata
                        </Button>
                        <button
                            onClick={() => toggleHidden(customer)}
                            className={`p-1.5 rounded ${
                                customer.is_hidden 
                                    ? 'text-green-600 bg-green-100 hover:bg-green-200' 
                                    : 'text-zinc-400 hover:text-green-600 hover:bg-green-50'
                            }`}
                            title={customer.is_hidden ? 'Client Sigur ✓' : 'Marchează ca Sigur'}
                        >
                            <Check size={16} />
                        </button>
                        <button
                            onClick={() => openModal(customer)}
                            className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded"
                            title="Editează"
                        >
                            <Edit2 size={16} />
                        </button>
                        <button
                            onClick={() => handleDelete(customer)}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
                            title="Șterge"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Customers;
